import React, { useState, useEffect, useMemo, useCallback, useContext } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { AuthContext } from '../shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmt = (n) => Number(n || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const isoDay = (d) => (d ? new Date(d).toISOString().slice(0, 10) : '');

const KIND_COLORS = {
  in: 'text-emerald-700 bg-emerald-50 border-emerald-200',
  out: 'text-rose-700 bg-rose-50 border-rose-200',
};

export const NewCashTransferDialog = ({ open, onOpenChange, agencyId, onSuccess }) => {
  const { user } = useContext(AuthContext);
  const isSuperAdmin = user?.role === 'super_admin';
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [items, setItems] = useState([]);
  const [totals, setTotals] = useState({ total_in: 0, total_out: 0, net: 0, current_balance: 0, count_in: 0, count_out: 0 });
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [search, setSearch] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [notes, setNotes] = useState('');
  // 🆕 Person physically carrying the cash to the general admin.
  const [recipientName, setRecipientName] = useState('');
  // 🆕 Confirmation view shown right after the transfer is created so
  // the employee can print the receipt to hand-off alongside the cash.
  const [created, setCreated] = useState(null); // { id, transfer_no, ... }
  const [pdfLoading, setPdfLoading] = useState(false);

  // 🆕 Archive-entry dialog state (super_admin only).
  const [archiveTarget, setArchiveTarget] = useState(null); // an item obj
  const [archiveReason, setArchiveReason] = useState('');
  const [archiving, setArchiving] = useState(false);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (agencyId) params.set('agency_id', agencyId);
      if (startDate) params.set('start_date', `${startDate}T00:00:00`);
      if (endDate) params.set('end_date', `${endDate}T23:59:59`);
      if (search) params.set('search', search);
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/cash-transfers/v2/available-entries?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setItems(data.items || []);
      setTotals({
        total_in: data.total_in || 0,
        total_out: data.total_out || 0,
        net: data.net || 0,
        current_balance: data.current_balance || 0,
        count_in: data.count_in || 0,
        count_out: data.count_out || 0,
      });
      setSelectedKeys(new Set());
    } catch (e) {
      toast.error(`فشل تحميل الوصولات: ${e.response?.data?.detail || e.message}`);
    } finally {
      setLoading(false);
    }
  }, [agencyId, startDate, endDate, search]);

  useEffect(() => {
    if (open) {
      // Reset the create-confirmation card whenever the dialog is
      // re-opened so a stale receipt link never leaks into a new
      // transfer session.
      setCreated(null);
      setRecipientName('');
      setNotes('');
      fetchItems();
    }
  }, [open, fetchItems]);

  const keyOf = (it) => `${it.kind}|${it.source_collection}|${it.source_id}`;

  const selectedItems = useMemo(
    () => items.filter((it) => selectedKeys.has(keyOf(it))),
    [items, selectedKeys],
  );
  const selectedIn = selectedItems.filter((it) => it.kind === 'in').reduce((s, it) => s + Number(it.amount || 0), 0);
  const selectedOut = selectedItems.filter((it) => it.kind === 'out').reduce((s, it) => s + Number(it.amount || 0), 0);
  const selectedNet = selectedIn - selectedOut;

  const toggle = (it) => {
    const k = keyOf(it);
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedKeys.size === items.length) setSelectedKeys(new Set());
    else setSelectedKeys(new Set(items.map(keyOf)));
  };

  const submit = async () => {
    if (selectedItems.length === 0) {
      toast.error('اختر وصلاً واحداً على الأقل');
      return;
    }
    if (!recipientName.trim()) {
      toast.error('اسم المستلم / حامل المبلغ مطلوب');
      return;
    }
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        entries: selectedItems.map((it) => ({
          kind: it.kind,
          source_collection: it.source_collection,
          source_id: it.source_id,
        })),
        notes: notes.trim() || null,
        recipient_name: recipientName.trim(),
      };
      const { data } = await axios.post(`${API}/cash-transfers/v2`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success(`✅ تم إنشاء التحويل ${data.transfer.transfer_no}`);
      // 🆕 Instead of closing immediately, show the confirmation card
      // so the employee can print the receipt to attach with the cash.
      setCreated(data.transfer);
      onSuccess && onSuccess(data.transfer);
    } catch (e) {
      toast.error(`فشل إنشاء التحويل: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSubmitting(false);
    }
  };

  // 🆕 Archive a single row (super_admin only). Idempotent server-side.
  const archiveEntry = async () => {
    if (!archiveTarget) return;
    const reason = archiveReason.trim();
    if (reason.length < 3) {
      toast.error('يرجى إدخال سبب الأرشفة (3 أحرف على الأقل)');
      return;
    }
    setArchiving(true);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/cash-transfers/v2/archive-entry`,
        {
          kind: archiveTarget.kind,
          source_collection: archiveTarget.source_collection,
          source_id: archiveTarget.source_id,
          fe_id: archiveTarget.fe_id || null,
          reason,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(data.message || 'تم الأرشفة');
      setArchiveTarget(null);
      setArchiveReason('');
      // Refresh the table so the archived row disappears immediately.
      await fetchItems();
    } catch (e) {
      toast.error(`فشل الأرشفة: ${e.response?.data?.detail || e.message}`);
    } finally {
      setArchiving(false);
    }
  };

  const downloadReceiptPdf = async () => {
    if (!created?.id) return;
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/cash-transfers/v2/${created.id}/receipt.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `receipt_${created.transfer_no}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(`تعذر تحميل الإيصال: ${e.message}`);
    } finally { setPdfLoading(false); }
  };

  const printReceiptPdf = async () => {
    if (!created?.id) return;
    setPdfLoading(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API}/cash-transfers/v2/${created.id}/receipt.pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      // Open in a hidden iframe so the print dialog appears in-page.
      const win = window.open(url, '_blank');
      if (win) {
        win.addEventListener('load', () => {
          try { win.print(); } catch (err) { /* pop-up blocked or CORS */ }
        });
      }
    } catch (e) {
      toast.error(`تعذر فتح الإيصال للطباعة: ${e.message}`);
    } finally { setPdfLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[92vh] overflow-hidden flex flex-col" data-testid="new-cash-transfer-dialog">
        <DialogHeader>
          <DialogTitle className="text-2xl">📤 تحويل نقدي جديد — إلى الإدارة العامة</DialogTitle>
          <p className="text-sm text-gray-500">
            اختر الوصولات (داخل + خارج) التي تريد تحويلها. الصافي هو المبلغ الذي سيصل للإدارة نقداً.
          </p>
        </DialogHeader>

        {/* Filters */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end border rounded-lg p-3 bg-gray-50">
          <div>
            <Label className="text-xs">من تاريخ</Label>
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} data-testid="ct-v2-from-date" />
          </div>
          <div>
            <Label className="text-xs">إلى تاريخ</Label>
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} data-testid="ct-v2-to-date" />
          </div>
          <div>
            <Label className="text-xs">بحث (رقم، عميل، وصف…)</Label>
            <Input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && fetchItems()} data-testid="ct-v2-search" />
          </div>
          <Button onClick={fetchItems} disabled={loading} data-testid="ct-v2-apply-filters">
            {loading ? '⏳ جارٍ التحميل…' : '🔍 تطبيق الفلاتر'}
          </Button>
        </div>

        {/* 💡 Prominent balance-vs-selection banner.
            The invariant the user cares about: "if I tick every row, the
            selected net MUST equal the current cashbox balance". */}
        <div className={`my-3 p-4 rounded-lg border-2 flex flex-wrap items-center justify-between gap-3 ${
          Math.abs(selectedNet - totals.current_balance) < 0.01 && selectedItems.length > 0
            ? 'bg-emerald-50 border-emerald-400'
            : selectedItems.length === 0
              ? 'bg-amber-50 border-amber-300'
              : 'bg-blue-50 border-blue-300'
        }`}>
          <div className="flex-1 min-w-[220px]">
            <div className="text-xs text-gray-600">💰 الرصيد الحالي في الخزينة</div>
            <div className="text-2xl font-bold text-gray-900" data-testid="ct-v2-current-balance">
              {fmt(totals.current_balance)} <span className="text-sm font-normal">د.ج</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              (كل ما هو <strong>{fmt(totals.total_in)}</strong> داخل &minus; <strong>{fmt(totals.total_out)}</strong> خارج)
            </div>
          </div>

          <div className="text-3xl text-gray-400">=</div>

          <div className="flex-1 min-w-[220px]">
            <div className="text-xs text-gray-600">✔ الصافي الذي ستحوّله الآن</div>
            <div className={`text-2xl font-bold ${selectedNet >= 0 ? 'text-emerald-800' : 'text-red-800'}`} data-testid="ct-v2-selected-net">
              {fmt(selectedNet)} <span className="text-sm font-normal">د.ج</span>
            </div>
            <div className="text-xs text-gray-500 mt-1">
              (<strong>{fmt(selectedIn)}</strong> داخل &minus; <strong>{fmt(selectedOut)}</strong> خارج — {selectedItems.length} وصل)
            </div>
          </div>

          <div className="w-full md:w-auto md:text-right">
            {selectedItems.length === 0 ? (
              <span className="inline-block bg-amber-500 text-white text-sm font-bold px-3 py-1.5 rounded">
                ابدأ باختيار الوصولات
              </span>
            ) : Math.abs(selectedNet - totals.current_balance) < 0.01 ? (
              <span className="inline-block bg-emerald-600 text-white text-sm font-bold px-3 py-1.5 rounded" data-testid="ct-v2-match-ok">
                ✅ تطابق تام مع الرصيد الحالي
              </span>
            ) : (
              <span className="inline-block bg-blue-500 text-white text-sm font-bold px-3 py-1.5 rounded" data-testid="ct-v2-match-partial">
                🔶 فارق: {fmt(totals.current_balance - selectedNet)} د.ج
              </span>
            )}
          </div>
        </div>

        {/* Detailed totals grid (kept for granular reference) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          <div className="p-2 rounded border bg-emerald-50 border-emerald-200 text-center">
            <div className="text-xs text-emerald-700">إجمالي الداخل المتاح</div>
            <div className="text-sm font-semibold text-emerald-800">{fmt(totals.total_in)}</div>
            <div className="text-[10px] text-emerald-600">{totals.count_in} وصل</div>
          </div>
          <div className="p-2 rounded border bg-rose-50 border-rose-200 text-center">
            <div className="text-xs text-rose-700">إجمالي الخارج المتاح</div>
            <div className="text-sm font-semibold text-rose-800">{fmt(totals.total_out)}</div>
            <div className="text-[10px] text-rose-600">{totals.count_out} وصل</div>
          </div>
          <div className="p-2 rounded border bg-emerald-100 border-emerald-300 text-center">
            <div className="text-xs text-emerald-800">✔ المختار داخل</div>
            <div className="text-sm font-semibold text-emerald-900">{fmt(selectedIn)}</div>
          </div>
          <div className="p-2 rounded border bg-rose-100 border-rose-300 text-center">
            <div className="text-xs text-rose-800">✔ المختار خارج</div>
            <div className="text-sm font-semibold text-rose-900">{fmt(selectedOut)}</div>
          </div>
        </div>

        {/* Items table */}
        <div className="flex-1 overflow-auto border rounded-lg">
          <table className="w-full text-sm">
            <thead className="bg-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-2 py-2 text-center">
                  <Checkbox
                    checked={items.length > 0 && selectedKeys.size === items.length}
                    onCheckedChange={toggleAll}
                    data-testid="ct-v2-select-all"
                  />
                </th>
                <th className="px-2 py-2 text-right">التاريخ</th>
                <th className="px-2 py-2 text-right">العميل</th>
                <th className="px-2 py-2 text-right">الموظف</th>
                <th className="px-2 py-2 text-right">نوع الخدمة</th>
                <th className="px-2 py-2 text-center">الاتجاه</th>
                <th className="px-2 py-2 text-right">المبلغ</th>
                {isSuperAdmin && (
                  <th className="px-2 py-2 text-center w-24">إجراءات</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isSuperAdmin ? 8 : 7} className="text-center py-8 text-gray-500">⏳ جارٍ التحميل…</td></tr>
              )}
              {!loading && items.length === 0 && (
                <tr><td colSpan={isSuperAdmin ? 8 : 7} className="text-center py-8 text-gray-500">لا توجد وصولات متاحة في هذا النطاق</td></tr>
              )}
              {!loading && items.map((it) => {
                const k = keyOf(it);
                const isSelected = selectedKeys.has(k);
                return (
                  <tr
                    key={k}
                    className={`border-t hover:bg-blue-50 cursor-pointer ${isSelected ? 'bg-blue-50' : ''}`}
                    onClick={() => toggle(it)}
                    data-testid={`ct-v2-row-${it.source_id}`}
                  >
                    <td className="px-2 py-2 text-center">
                      <Checkbox checked={isSelected} onCheckedChange={() => toggle(it)} onClick={(e) => e.stopPropagation()} />
                    </td>
                    <td className="px-2 py-2 whitespace-nowrap">{isoDay(it.date)}</td>
                    <td className="px-2 py-2">{it.client_name || '—'}</td>
                    <td className="px-2 py-2">{it.employee_name || '—'}</td>
                    <td className="px-2 py-2">{it.service_name || '—'}</td>
                    <td className="px-2 py-2 text-center">
                      <Badge variant="outline" className={KIND_COLORS[it.kind]}>
                        {it.kind === 'in' ? '⬇ داخل' : '⬆ خارج'}
                      </Badge>
                    </td>
                    <td className={`px-2 py-2 font-semibold text-right ${it.kind === 'in' ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {it.kind === 'in' ? '+' : '−'}{fmt(it.amount)}
                    </td>
                    {isSuperAdmin && (
                      <td className="px-2 py-2 text-center">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="text-amber-700 border-amber-300 hover:bg-amber-50 h-7 px-2"
                          onClick={(e) => {
                            e.stopPropagation();
                            setArchiveTarget(it);
                            setArchiveReason('');
                          }}
                          data-testid={`ct-v2-archive-btn-${it.source_id}`}
                          title="أرشفة هذا الوصل — لن يظهر للموظف في التحويلات القادمة"
                        >
                          🗄️ أرشفة
                        </Button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Notes + recipient + submit */}
        {!created && (
        <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
          <div>
            <Label className="text-xs font-bold">
              اسم المستلم / حامل المبلغ <span className="text-red-500">*</span>
            </Label>
            <Input
              value={recipientName}
              onChange={(e) => setRecipientName(e.target.value)}
              placeholder="مثال: السيد أحمد بن علي"
              data-testid="ct-v2-recipient"
            />
            <p className="text-[10px] text-gray-500 mt-0.5">
              يُطبع على الإيصال ليُوقّعه المستلم عند تسليم النقد.
            </p>
          </div>
          <div>
            <Label className="text-xs">ملاحظات (اختياري)</Label>
            <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="مثال: تحويل نهاية الأسبوع" data-testid="ct-v2-notes" />
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ct-v2-cancel">إلغاء</Button>
            <Button onClick={submit} disabled={submitting || selectedItems.length === 0 || !recipientName.trim()} data-testid="ct-v2-submit">
              {submitting ? '⏳ جارٍ الإرسال…' : `✔ إرسال التحويل (${selectedItems.length})`}
            </Button>
          </div>
        </div>
        )}

        {/* 🆕 Post-create confirmation card — replaces the form once the
            transfer is saved so the employee can print / download the
            receipt without navigating away. */}
        {created && (
          <div
            className="mt-3 p-4 rounded-lg border-2 border-emerald-400 bg-emerald-50"
            data-testid="ct-v2-created-card"
          >
            <div className="flex items-start gap-3">
              <div className="text-4xl">✅</div>
              <div className="flex-1">
                <div className="text-lg font-bold text-emerald-900">
                  تم إنشاء التحويل <span className="font-mono">{created.transfer_no}</span>
                </div>
                <div className="text-sm text-emerald-800 mt-0.5">
                  الصافي المُحوَّل: <strong>{fmt(created.amount_net)} د.ج</strong>
                  {created.recipient_name && (
                    <> — يُسلَّم عبر: <strong>{created.recipient_name}</strong></>
                  )}
                </div>
                <div className="text-xs text-emerald-700 mt-1">
                  اطبع الإيصال أدناه وأرفقه مع النقد ليوقّعه المستلم عند التسليم.
                </div>
              </div>
            </div>
            <div className="mt-3 flex flex-wrap gap-2 justify-end">
              <Button
                variant="outline"
                onClick={downloadReceiptPdf}
                disabled={pdfLoading}
                className="border-indigo-500 text-indigo-700 hover:bg-indigo-50"
                data-testid="ct-v2-download-receipt"
              >
                {pdfLoading ? '⏳' : '📥 تحميل PDF'}
              </Button>
              <Button
                onClick={printReceiptPdf}
                disabled={pdfLoading}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="ct-v2-print-receipt"
              >
                {pdfLoading ? '⏳' : '🖨 طباعة الإيصال'}
              </Button>
              <Button
                onClick={() => onOpenChange(false)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="ct-v2-close-after-create"
              >
                ✓ تم — إغلاق
              </Button>
            </div>
          </div>
        )}
      </DialogContent>

      {/* 🆕 Archive-entry confirmation dialog — super_admin only. */}
      <Dialog open={!!archiveTarget} onOpenChange={(o) => { if (!o) { setArchiveTarget(null); setArchiveReason(''); } }}>
        <DialogContent className="max-w-lg" dir="rtl" data-testid="ct-v2-archive-dialog">
          <DialogHeader>
            <DialogTitle className="text-lg font-bold text-amber-800">
              🗄️ أرشفة وصل — يخرج من الرصيد وقائمة التحويل
            </DialogTitle>
          </DialogHeader>
          {archiveTarget && (
            <div className="space-y-3">
              <div className="p-3 rounded border-2 border-amber-200 bg-amber-50 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <div><span className="text-gray-600">التاريخ:</span> <strong>{isoDay(archiveTarget.date)}</strong></div>
                  <div><span className="text-gray-600">المبلغ:</span> <strong className={archiveTarget.kind === 'in' ? 'text-emerald-700' : 'text-rose-700'}>{archiveTarget.kind === 'in' ? '+' : '−'}{fmt(archiveTarget.amount)} د.ج</strong></div>
                  <div><span className="text-gray-600">العميل:</span> <strong>{archiveTarget.client_name || '—'}</strong></div>
                  <div><span className="text-gray-600">النوع:</span> <strong>{archiveTarget.service_name || '—'}</strong></div>
                </div>
              </div>
              <div className="p-2.5 rounded border border-red-200 bg-red-50 text-xs text-red-800">
                ⚠️ بعد الأرشفة، هذا الوصل:
                <ul className="list-disc pr-5 mt-1 space-y-0.5">
                  <li>لن يظهر في نافذة التحويل النقدي الجديد للموظف.</li>
                  <li>سيُستَبعد من رصيد الوكالة القابل للتحويل.</li>
                  <li>يمكن استعادته لاحقاً بواسطتك أو بواسطة المحاسب العام.</li>
                </ul>
              </div>
              <div>
                <Label className="text-xs font-bold">
                  سبب الأرشفة <span className="text-red-500">*</span>
                </Label>
                <Input
                  value={archiveReason}
                  onChange={(e) => setArchiveReason(e.target.value)}
                  placeholder="مثال: مكرر مع وصل رقم … / خطأ إدخال / وصل ملغى"
                  data-testid="ct-v2-archive-reason"
                />
                <p className="text-[10px] text-gray-500 mt-0.5">
                  يُسجّل في السجل المالي للتدقيق. مطلوب — على الأقل 3 أحرف.
                </p>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button
                  variant="outline"
                  onClick={() => { setArchiveTarget(null); setArchiveReason(''); }}
                  disabled={archiving}
                  data-testid="ct-v2-archive-cancel"
                >
                  إلغاء
                </Button>
                <Button
                  onClick={archiveEntry}
                  disabled={archiving || archiveReason.trim().length < 3}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="ct-v2-archive-confirm"
                >
                  {archiving ? '⏳ جارٍ الأرشفة…' : '🗄️ تأكيد الأرشفة'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Dialog>
  );
};

export default NewCashTransferDialog;
